/**
 * Course content session cache
 *
 * Holds successful Firestore course preview payloads in memory for the browser tab so
 * welcome and install screens do not refetch the same course.
 *
 * References:
 * - `hooks/useCourseContent.ts`
 */

export type CourseContentReady = {
  status: 'ready';
  heroImageSrc: string;
  thumbnailSrcs: string[];
  /** 800px hero URL per thumbnail index; aligned with thumbnailSrcs. */
  heroImageSrcs: string[];
  previewDescription: string;
  previewBulletPoints: string[];
};

export type CourseContentResult =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | CourseContentReady;

/** In-memory session cache for course preview content (tab lifetime). */
const sessionCache = new Map<string, CourseContentReady>();

/**
 * Returns a cached ready payload for the course id, if present.
 *
 * @param {string} courseId Firestore course document id.
 * @return {Object | undefined} Cached ready content or undefined.
 */
export function getCachedCourseContent(courseId: string): CourseContentReady | undefined {
  const id = courseId.trim();
  if (!id) {
    return undefined;
  }
  return sessionCache.get(id);
}

/**
 * Stores a ready fetch result in the tab session cache.
 *
 * @param {string} courseId Firestore course document id.
 * @param {Object} result Hook fetch result; only `ready` is cached.
 * @return {void}
 */
export function setCachedCourseContent(courseId: string, result: CourseContentResult): void {
  const id = courseId.trim();
  if (!id || result.status !== 'ready') {
    return;
  }
  sessionCache.set(id, result);
}
